import { onBeforeUnmount, onMounted, type Ref } from "vue";

/**
 * Trap keyboard focus inside a container while it is mounted, and hand focus
 * back to whatever opened it on teardown.
 *
 * Shared by the slide-over and the session overlay so the trap exists in one
 * place rather than being reimplemented (and drifting) in two. Both surfaces
 * are modal: an untrapped one lets Tab walk into the page behind, which for a
 * full-screen session overlay means a student can tab into a form they cannot
 * see.
 *
 * Escape is deliberately *not* handled here — the overlay needs to guard it
 * (confirm before discarding a running focus block) while the slide-over closes
 * immediately, so the key belongs to the caller.
 */

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * Hidden-but-present controls must not become invisible tab stops.
 *
 * The check is deliberately layout-free. `offsetParent` needs a layout engine:
 * it is null for every element under jsdom, and null for fixed-position
 * elements in some browsers — either of which silently collapses the trap to a
 * single element and lets Tab escape. Vue's `v-if` removes nodes outright, so
 * attribute checks cover the realistic remainder.
 */
function isHidden(el: HTMLElement): boolean {
  return el.hasAttribute("hidden") || el.getAttribute("aria-hidden") === "true";
}

export function useFocusTrap(container: Ref<HTMLElement | null>): void {
  let previouslyFocused: HTMLElement | null = null;

  function focusable(): HTMLElement[] {
    const root = container.value;
    if (!root) return [];
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => !isHidden(el),
    );
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.key !== "Tab") return;
    const items = focusable();
    if (items.length === 0) {
      // Nothing to focus: pin focus to the container rather than letting Tab
      // escape to the page behind.
      event.preventDefault();
      container.value?.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === container.value)) {
      event.preventDefault();
      last.focus();
    } else if (
      !event.shiftKey &&
      (active === last || active === container.value)
    ) {
      event.preventDefault();
      first.focus();
    }
  }

  onMounted(() => {
    previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const root = container.value;
    if (root) {
      if (!root.hasAttribute("tabindex")) root.setAttribute("tabindex", "-1");
      const items = focusable();
      // Prefer a real control so a keyboard user lands somewhere actionable;
      // fall back to the container so focus is at least inside the trap.
      (items[0] ?? root).focus();
    }
    document.addEventListener("keydown", onKeydown, true);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("keydown", onKeydown, true);
    // Returning focus to the trigger is what makes the surface feel like a
    // detour rather than a navigation.
    previouslyFocused?.focus();
  });
}
